import { Menu } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { STATUS } from '../../functions/visibility';
import { VsPeerGroupsMenuItem } from '../menus/dotCTAs/VsPeerGroupsMenuItem';
import { setComparisonFilters } from '../../functions/filtering';
import { MoreDetailsMenuItem } from '../menus/dotCTAs/MoreDetailsMenuItem';
import { useAuth0 } from '@auth0/auth0-react';
import { FilterStatesContext } from '../contexts/filterStates';
import { useWindow } from '../../hooks/window';
import RankingTable from './RankingTable';
import { MenuSlide } from '../transitions/MenuSlide';

/**
 * Wrapper for the RankingTable component which introduces functionality required for desktop screen sizes.
 * Tracks state for selected rows, defines event listeners and handles more info menu popover.
 *
 * @param filteredData
 * @param selectedPortfolio
 * @param setSelectedPortfolio
 * @param onMoreDetailsClick
 * @returns {JSX.Element}
 * @constructor
 */
const DesktopRankingTable = ({
    filteredData,
    selectedPortfolio,
    setSelectedPortfolio,
    onMoreDetailsClick
}) => {
    const windowSize = useWindow();
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    /* Close menu popover on window resize */
    useEffect(() => {
        handleMenuClose();
    }, [windowSize]);

    const { peerFilterDispatch, setSearchState } =
        useContext(FilterStatesContext);

    /* State for tracking row selection within component */
    const [selectedRow, setSelectedRow] = useState(null);

    /* Creates event listener to allow clicking away from selected row by clicking anywhere else (except for another row) */
    useEffect(() => {
        const rowClicked = selectedRow && !selectedRow.isKebabClick;

        const handleClickOutside = (event) => {
            if (rowClicked && !event.target.closest('.MuiTableCell-root')) {
                setSelectedRow(null);
                setSelectedPortfolio(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [selectedRow]);

    /**
     * Called when more info kebab clicked
     * @param {Element} target - target element where menu is to be anchored
     * @param {Object} row - data for row in question
     */
    const handleKebabClick = (target, row) => {
        setAnchorEl(target);
        setSelectedRow({ row: row, isKebabClick: true });
        setSelectedPortfolio({ row: row, isClicked: true });
    };

    /* Close more info menu */
    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
        setSelectedPortfolio(null);
    };

    const { user } = useAuth0();
    const subscriptionType = user?.subscription_type ?? 'unauthorized';

    /**
     * Checks whether selected row's More Details menu option should be disabled
     * @returns {boolean} - true if disabled
     */
    const isMoreDetailsDisabled = () => {
        return (
            subscriptionType !== 'basic' &&
            selectedRow?.row.status < STATUS.open
        );
    };

    //Track when table scrolls, so we don't try to interact with graph at the same time, which degrades performance
    const [isScrolling, setIsScrolling] = useState(false);

    /* Row mouse-over behaviour */
    const handleRowMouseOver = (row) => {
        if (!isScrolling && !selectedRow && !selectedPortfolio?.isClicked) {
            setSelectedPortfolio({ row: row, isClicked: false });
        }
    };

    /* Row mouse-out behaviour */
    const handleRowMouseOut = () => {
        if (!isScrolling && !selectedRow && !selectedPortfolio?.isClicked) {
            setSelectedPortfolio(null);
        }
    };

    /* Row click behaviour */
    const handleRowClick = (event, row) => {
        if (!event.target.closest('.MuiButtonBase-root')) {
            if (selectedRow?.row.publicToken === row.publicToken) {
                //re-click of selected row, de-selects it
                setSelectedRow(null);
                setSelectedPortfolio(null);
            } else {
                setSelectedPortfolio({ row: row, isClicked: true });
                setSelectedRow({ row: row, isKebabClick: false });
            }
        }
    };

    /* Called when Vs Peer Group menu option is clicked */
    const handleVsPeerGroupClick = () => {
        const row = selectedRow.row;
        setSearchState(null);
        setComparisonFilters(selectedRow.row, peerFilterDispatch);
        setAnchorEl(null);

        setSelectedRow({ row: row, isKebabClick: false });
        setSelectedPortfolio({ row: row, isClicked: true });
    };

    /* Called when More Details menu option is clicked */
    const handleMoreDetailsClick = () => {
        handleMenuClose();
        onMoreDetailsClick(selectedRow.row);
    };

    return (
        <>
            <RankingTable
                filteredData={filteredData}
                selectedPortfolio={selectedPortfolio}
                onRowMouseOver={handleRowMouseOver}
                onRowMouseOut={handleRowMouseOut}
                onRowClick={handleRowClick}
                onKebabClick={handleKebabClick}
                selectedRow={selectedRow}
                tableVirtuosoProps={{
                    isScrolling: (b) => setIsScrolling(b)
                }}
            />
            <Menu
                MenuListProps={{
                    'aria-label': 'more options menu',
                    'data-analyticsid': 'more-options-menu'
                }}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 3,
                    horizontal: 'right'
                }}
                PaperProps={{
                    sx: {
                        width: { sm: 150, md: 175, lg: 200 },
                        borderRadius: '0px 0px 8px 8px',
                        boxShadow: '-4px 4px 4px 0px #00000040'
                    }
                }}
                TransitionComponent={MenuSlide}
            >
                <VsPeerGroupsMenuItem
                    onClick={handleVsPeerGroupClick}
                    disabled={subscriptionType === 'unauthorized'}
                />
                <MoreDetailsMenuItem
                    onClick={handleMoreDetailsClick}
                    disabled={isMoreDetailsDisabled()}
                />
            </Menu>
        </>
    );
};
export default DesktopRankingTable;

DesktopRankingTable.propTypes = {
    filteredData: PropTypes.array,
    onMoreDetailsClick: PropTypes.func,
    selectedPortfolio: PropTypes.object,
    setSelectedPortfolio: PropTypes.func
};
